import React, { ReactNode } from 'react'

import type {
  TAnalysisCalculationRowDto,
  TAnalysisResultDto
} from '@homepal/api-contract'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'

import SidebarDialog from 'components_new/molecules/SidebarDialog'

import { formatValue } from 'components_new/organisms/Widget/Types/utils'

interface PotentialBreakdownDialogProps {
  calculation: TAnalysisCalculationRowDto[]
  onClose: () => void
  open: boolean
  potential: number
  result: TAnalysisResultDto | null
}

const SECONDARY = 'text.secondary'

const Cell = (props: {
  children?: ReactNode
  sx?: Record<string, unknown>
}) => <Box sx={{ ...props.sx }}>{props.children}</Box>

// A rule of its own across the grid: on the cells it would follow each
// cell's baseline and break into steps.
const Rule = (props: { color: string; sx?: Record<string, unknown> }) => (
  <Box
    sx={{
      borderTop: '1px solid',
      borderColor: props.color,
      gridColumn: '1 / -1',
      ...props.sx
    }}
  />
)

/** How the potential is reached, set out step by step as on paper. */
const PotentialBreakdownDialog = (props: PotentialBreakdownDialogProps) => {
  const { calculation, onClose, open, potential, result } = props

  const { t } = useTranslation()

  const green = 'success.text'
  const total = `${potential > 0 ? '+' : ''}${formatValue(potential, null, 0)}`

  const operator = {
    color: 'text.primary',
    fontFamily: '"DM Sans", sans-serif',
    fontSize: 15,
    fontWeight: 700,
    textAlign: 'center'
  }

  const renderRow = (row: TAnalysisCalculationRowDto, i: number) => {
    const sum = row.kind === 'sum'
    const line = sum ? { pb: '12px', pt: '9px' } : { py: '5px' }

    return (
      <React.Fragment key={`${row.label}-${i}`}>
        {sum ? <Rule color="divider" /> : null}
        <Cell
          sx={{
            ...line,
            color: sum ? 'text.primary' : SECONDARY,
            fontWeight: sum ? 600 : 400,
            whiteSpace: 'nowrap'
          }}
        >
          {row.label}
        </Cell>
        <Cell sx={{ ...line, ...operator }}>{sum ? '=' : row.op ?? ''}</Cell>
        <Cell
          sx={{
            ...line,
            fontWeight: 600,
            pl: '12px',
            textAlign: 'right',
            whiteSpace: 'nowrap'
          }}
        >
          {row.value === null
            ? '–'
            : formatValue(row.value, null, row.decimals)}
        </Cell>
        <Cell sx={{ ...line, color: SECONDARY, pl: '6px', pr: '32px' }}>
          {row.unit}
        </Cell>
        <Cell sx={{ ...line, color: SECONDARY, fontSize: 12, lineHeight: 1.5 }}>
          {row.description}
        </Cell>
      </React.Fragment>
    )
  }

  const resultLine = { pt: '10px' }

  return (
    <SidebarDialog
      actions={
        <Button onClick={onClose} variant="text">
          {t('global', 'close')}
        </Button>
      }
      maxWidth="md"
      onClose={onClose}
      open={open}
      title={t('analyses', 'report_potential_result')}
    >
      <Box
        sx={{
          alignItems: 'baseline',
          display: 'grid',
          fontSize: 13,
          fontVariantNumeric: 'tabular-nums',
          gridTemplateColumns: '230px 22px 96px auto minmax(0, 1fr)',
          letterSpacing: '-0.01em',
          lineHeight: 1.4,
          px: 2
        }}
      >
        {calculation.map(renderRow)}

        <Rule color="text.primary" sx={{ mt: '6px' }} />
        <Cell sx={{ ...resultLine, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {t('analyses', 'report_potential_result')}*
        </Cell>
        <Cell sx={{ ...resultLine, ...operator, color: green }}>≈</Cell>
        <Cell
          sx={{
            ...resultLine,
            color: green,
            fontWeight: 600,
            pl: '12px',
            textAlign: 'right',
            whiteSpace: 'nowrap'
          }}
        >
          {total}
        </Cell>
        <Cell sx={{ ...resultLine, color: green, pl: '6px', pr: '32px' }}>
          kr/år
        </Cell>
        <Cell
          sx={{
            ...resultLine,
            color: SECONDARY,
            fontSize: 12,
            lineHeight: 1.5
          }}
        >
          {result?.description}
        </Cell>
      </Box>
      <Box sx={{ color: SECONDARY, fontSize: 12, mt: 2, px: 2 }}>
        * {t('analyses', 'potential_rounding_note')}
      </Box>
    </SidebarDialog>
  )
}

export default PotentialBreakdownDialog
